import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Group,
  TextInput,
  ActionIcon,
  Avatar,
  Menu,
  Text,
  Paper,
  Stack,
  UnstyledButton,
  Image,
  Center,
  Loader,
  Button,
} from '@mantine/core';
import {
  IconSearch,
  IconSun,
  IconMoon,
  IconUser,
  IconSettings,
  IconLogout,
  IconShield,
  IconChevronDown,
  IconPackage,
  IconStar,
} from '@tabler/icons-react';
import { useDebouncedValue, useClickOutside } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Logo } from '../common/Logo';
import { NotificationBell } from './NotificationBell';
import { searchService } from '../../services/search';

export const NAVBAR_HEIGHT = 62;

const NAV_BG = 'var(--mantine-color-body)';
const NAV_BORDER = 'light-dark(var(--mantine-color-default-border), rgba(255,255,255,0.08))';
const NAV_LABEL_COLOR = 'light-dark(var(--mantine-color-dark-5), rgba(255,255,255,0.8))';
const LOGO_COL_WIDTH = 280;   // matches LEFT_NAV_WIDTH in ThreeColumnLayout
const ACTIONS_COL_WIDTH = 340; // matches RIGHT_SIDEBAR_WIDTH in ThreeColumnLayout
const NAV_H_PAD = 12;          // matches GAP in ThreeColumnLayout



export function TopNavbar() {
  const navigate = useNavigate();
  const { isAuthenticated, user, isModerator, logout } = useAuth();
  const { activeColorScheme, toggleColorScheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(searchQuery, 300);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useClickOutside(() => setShowDropdown(false));

  const { data, isFetching } = useQuery({
    queryKey: ['navbar-search', debouncedQuery],
    queryFn: () => searchService.search(debouncedQuery),
    enabled: !!debouncedQuery.trim(),
    staleTime: 30_000,
    select: (d) => ({
      reviews: d.reviews.slice(0, 3),
      products: d.products.slice(0, 3),
      users: d.users.slice(0, 2),
    }),
  });

  const hasResults = !!data && (
    data.reviews.length > 0 || data.products.length > 0 || data.users.length > 0
  );
  const showDropdownDerived = showDropdown && !!debouncedQuery.trim() && (isFetching || hasResults);

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleNavigate = (to: string) => {
    navigate(to);
    setSearchQuery('');
    setShowDropdown(false);
  };

  return (
    <Box
      component="header"
      style={{
        backgroundColor: NAV_BG,
        borderBottom: `1px solid ${NAV_BORDER}`,
        position: 'sticky',
        top: 0,
        zIndex: 200,
        width: '100%',
      }}
    >
      {/* ── Main row: logo | search | actions ── */}
      <Box
        style={{
          maxWidth: 1600,
          margin: '0 auto',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          padding: `0.75rem ${NAV_H_PAD}px`,
          gap: `${NAV_H_PAD}px`,
        }}
      >
        <Box style={{ flexShrink: 0, width: LOGO_COL_WIDTH }}>
          <Logo size="md" showText />
        </Box>

        {/* Search */}
        <Box style={{ flex: 1, position: 'relative' }} ref={searchRef}>
          <form onSubmit={handleSearch}>
            <TextInput
              placeholder="Search reviews, products, people..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => { if (searchQuery.trim()) setShowDropdown(true); }}
              onKeyDown={(e) => { if (e.key === 'Escape') setShowDropdown(false); }}
              leftSection={<IconSearch size={16} />}
              radius="xl"
              size="sm"
            />
          </form>

          {/* Dropdown */}
          {showDropdownDerived && (
            <Paper
              shadow="xl"
              radius="md"
              withBorder
              style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: 0,
                right: 0,
                zIndex: 300,
                overflow: 'hidden',
              }}
            >
              {isFetching && !hasResults ? (
                <Center py="sm"><Loader size="sm" /></Center>
              ) : (
                <Stack gap={0} style={{ maxHeight: 420, overflowY: 'auto' }}>
                  {data?.reviews && data.reviews.length > 0 && (
                    <>
                      <Text size="xs" c="dimmed" fw={600} tt="uppercase" px="sm" pt="xs" pb={4}>Reviews</Text>
                      {data.reviews.map((review) => (
                        <UnstyledButton key={review.id} py={8} px="sm" onMouseDown={() => handleNavigate(`/reviews/${review.id}`)}>
                          <Group gap="sm" wrap="nowrap">
                            <Center w={32} h={32} style={{ borderRadius: 'var(--mantine-radius-sm)', backgroundColor: 'var(--mantine-color-gray-1)', flexShrink: 0, overflow: 'hidden' }}>
                              {review.productImageUrl
                                ? <Image src={review.productImageUrl} alt="" w={32} h={32} fit="contain" />
                                : <IconPackage size={14} color="var(--mantine-color-dimmed)" />}
                            </Center>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text size="sm" fw={500} truncate>{review.productName}</Text>
                              <Group gap={4}>
                                <IconStar size={11} color="var(--mantine-color-yellow-5)" fill="var(--mantine-color-yellow-5)" />
                                <Text size="xs" c="dimmed">{review.rating}/5 · @{review.username}</Text>
                              </Group>
                            </Box>
                          </Group>
                        </UnstyledButton>
                      ))}
                    </>
                  )}

                  {data?.users && data.users.length > 0 && (
                    <>
                      <Text size="xs" c="dimmed" fw={600} tt="uppercase" px="sm" pt="xs" pb={4}>People</Text>
                      {data.users.map((u) => (
                        <UnstyledButton key={u.id} py={6} px="sm" onMouseDown={() => handleNavigate(`/users/${u.username}`)}>
                          <Group gap="sm" wrap="nowrap">
                            <Avatar src={u.profileImageUrl} size={28} radius="xl"><IconUser size={12} /></Avatar>
                            <Text size="sm" fw={500} truncate>@{u.username}</Text>
                          </Group>
                        </UnstyledButton>
                      ))}
                    </>
                  )}

                  {data?.products && data.products.length > 0 && (
                    <>
                      <Text size="xs" c="dimmed" fw={600} tt="uppercase" px="sm" pt="xs" pb={4}>Products</Text>
                      {data.products.map((p) => (
                        <UnstyledButton key={p.id} py={6} px="sm" onMouseDown={() => handleNavigate(`/products/${p.id}`)}>
                          <Group gap="sm" wrap="nowrap">
                            <Center w={28} h={28} style={{ borderRadius: 'var(--mantine-radius-sm)', backgroundColor: 'var(--mantine-color-gray-1)', flexShrink: 0, overflow: 'hidden' }}>
                              {p.imageUrl
                                ? <Image src={p.imageUrl} alt="" w={28} h={28} fit="contain" />
                                : <IconPackage size={12} color="var(--mantine-color-dimmed)" />}
                            </Center>
                            <Text size="sm" truncate>{p.name}</Text>
                          </Group>
                        </UnstyledButton>
                      ))}
                    </>
                  )}

                  <UnstyledButton
                    py="xs" px="sm"
                    onMouseDown={() => handleSearch()}
                    style={{
                      borderTop: '1px solid var(--mantine-color-default-border)',
                      display: 'flex', alignItems: 'center',
                      position: 'sticky', bottom: 0,
                      backgroundColor: 'var(--mantine-color-body)',
                    }}
                  >
                    <Group gap="xs">
                      <IconSearch size={13} />
                      <Text size="sm" c="blue">See all results for &ldquo;{searchQuery.trim()}&rdquo;</Text>
                    </Group>
                  </UnstyledButton>
                </Stack>
              )}
            </Paper>
          )}
        </Box>

        {/* Right actions */}
        <Group gap="sm" wrap="nowrap" align="center" style={{ flexShrink: 0, width: ACTIONS_COL_WIDTH, justifyContent: 'flex-end' }}>
          {isAuthenticated ? (
            <>
              <NotificationBell iconVariant="light" iconColor="brand" iconSize={36} />

              {/* User menu */}
              <Menu shadow="md" radius="md" width={200} position="bottom-end">
                <Menu.Target>
                  <UnstyledButton style={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar src={user?.profileImageUrl} size={36} radius="xl">
                      <IconUser size={16} />
                    </Avatar>
                    <Box
                      style={{
                        position: 'absolute',
                        bottom: -2,
                        right: -4,
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: 'var(--mantine-color-default-border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IconChevronDown size={9} />
                    </Box>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconUser size={14} />} component={Link} to={`/users/${user?.username}`}>Profile</Menu.Item>
                  <Menu.Item leftSection={<IconSettings size={14} />} component={Link} to="/settings">Settings</Menu.Item>
                  {isModerator && (
                    <>
                      <Menu.Divider />
                      <Menu.Item leftSection={<IconShield size={14} />} component={Link} to="/admin">Admin</Menu.Item>
                    </>
                  )}
                  <Menu.Divider />
                  <Menu.Item
                    leftSection={activeColorScheme === 'dark' ? <IconSun size={14} /> : <IconMoon size={14} />}
                    onClick={() => toggleColorScheme()}
                  >
                    {activeColorScheme === 'dark' ? 'Light mode' : 'Dark mode'}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item leftSection={<IconLogout size={14} />} color="red" onClick={() => logout()}>Sign out</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </>
          ) : (
            <>
              <ActionIcon
                onClick={() => toggleColorScheme()}
                size="md"
                radius="xl"
                variant="subtle"
                style={{ color: NAV_LABEL_COLOR }}
                title={activeColorScheme === 'dark' ? 'Light mode' : 'Dark mode'}
              >
                {activeColorScheme === 'dark' ? <IconSun size={16} /> : <IconMoon size={16} />}
              </ActionIcon>
              <Button component={Link} to="/login" variant="default" size="sm" radius="xl">
                Log in
              </Button>
              <Button component={Link} to="/register" size="sm" radius="xl">
                Sign up
              </Button>
            </>
          )}
        </Group>
      </Box>

    </Box>
  );
}
